// src/new/components/monitor/TriageChat/useTriageChat.ts
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useVedaChatSocket, ChatMessage } from './useVedaChatSocket';
import { useExpressChatSSE } from './useExpressChatSSE';
import { ServerEvent } from './vedaProtocol';
import { ExpressChatEvent } from './expressProtocol';
import { TriageContext, buildTriageSeed, buildTriageGuardrail } from './triageContext';
import { TriageBackend, HistoryMessage } from './triageBackend';

// Debounce window for a `getHistory` reconcile triggered by express-code's
// `history-refresh` signal or by a settled `/tail` resume — matches
// express-code frontend's own convention (see useExpressChatSSE.ts /
// expressProtocol.ts docs).
const HISTORY_RECONCILE_DEBOUNCE_MS = 1500;

type Action =
  | { type: 'init'; messages: ChatMessage[] }
  | { type: 'user'; text: string }
  | {
      type: 'text';
      messageId: string;
      text: string;
      done: boolean;
      replace?: boolean;
      // express-code only: OpenCode part id. When present, the reducer keys the
      // bubble by `messageId` and accumulates text per `partId` (so multiple
      // assistant messages in one turn stay separate). Absent for veda-ai,
      // which keeps the legacy append-to-last-assistant-bubble behavior.
      partId?: string | null;
    }
  | { type: 'tool'; label: string }
  | {
      type: 'subagent-start';
      childSessionId: string;
      agent: string | null;
      title: string | null;
      prompt: string | null;
    }
  | { type: 'subagent-text'; childSessionId: string; text: string; replace: boolean }
  | { type: 'subagent-end'; childSessionId: string }
  // A completed express-code turn: the express hook has already flipped its
  // OWN `streaming` to false, but the message-level `streaming` flag (which
  // gates the merge-history "don't clobber an in-flight bubble" rule below)
  // is only known to this reducer, so it needs its own terminal action.
  | { type: 'stream-end' }
  // A `backend.getHistory()` reconcile (from `history-refresh` or a settled
  // `/tail` resume) — replaces every SETTLED turn with the fresh server copy
  // but never touches a message this reducer still considers `streaming`.
  | { type: 'merge-history'; messages: ChatMessage[] };

function reducer(state: ChatMessage[], action: Action): ChatMessage[] {
  switch (action.type) {
    case 'init':
      return action.messages;
    case 'user':
      return [
        ...state,
        { id: `u-${state.length}`, role: 'user', text: action.text, createdAt: Date.now() },
      ];
    case 'tool':
      return [
        ...state,
        { id: `t-${state.length}`, role: 'tool', text: action.label, createdAt: Date.now() },
      ];
    case 'text': {
      // express-code path: a `partId` is present. OpenCode emits one or more
      // assistant MESSAGES per turn (distinct `messageId`), each with one or
      // more PARTS (distinct `partId`). Key the bubble by `messageId` so a
      // later message never overwrites an earlier one (the "only the last
      // complete message shows" bug), and accumulate text per `partId` —
      // deltas append, `part.updated` snaps (`action.replace`) — joining parts
      // in arrival order so a multi-part message doesn't clobber itself.
      if (action.partId) {
        const idx = state.findIndex((m) => m.role === 'assistant' && m.id === action.messageId);
        if (idx === -1) {
          // A `subagent-start` that arrived before any lead text (a
          // task-call-first turn) already opened a placeholder bubble with no
          // real `messageId` yet — adopt it instead of splitting the turn's
          // subagent card and lead text across two separate bubbles.
          const lastIdx = state.length - 1;
          const last = state[lastIdx];
          const isSubagentPlaceholder =
            last?.role === 'assistant' &&
            last.text === '' &&
            !last.expressParts &&
            !!last.subagents;
          if (isSubagentPlaceholder) {
            const next = state.slice();
            next[lastIdx] = {
              ...last,
              id: action.messageId,
              text: action.text,
              streaming: !action.done,
              expressParts: { [action.partId]: action.text },
              expressPartOrder: [action.partId],
            };
            return next;
          }
          // New assistant message → new bubble. Any earlier assistant bubble
          // still flagged streaming is now settled (messages arrive in order).
          const settled = state.map((m) =>
            m.role === 'assistant' && m.streaming ? { ...m, streaming: false } : m,
          );
          return [
            ...settled,
            {
              id: action.messageId,
              role: 'assistant',
              text: action.text,
              streaming: !action.done,
              createdAt: Date.now(),
              expressParts: { [action.partId]: action.text },
              expressPartOrder: [action.partId],
            },
          ];
        }
        const b = state[idx];
        const parts = { ...(b.expressParts ?? {}) };
        const order = b.expressPartOrder ? [...b.expressPartOrder] : [];
        if (!(action.partId in parts)) order.push(action.partId);
        parts[action.partId] = action.replace
          ? action.text
          : (parts[action.partId] ?? '') + action.text;
        const next = state.slice();
        next[idx] = {
          ...b,
          expressParts: parts,
          expressPartOrder: order,
          text: order.map((p) => parts[p]).join(''),
          streaming: !action.done,
        };
        return next;
      }
      // veda-ai streams DELTAS: each chunk is a separate message with a
      // different id but the same turn (conversation_message_order), and the
      // text is the new fragment (not cumulative). Append consecutive deltas
      // into the current assistant bubble; a non-assistant message (user/tool)
      // breaks the run so the next delta starts a fresh bubble. This yields one
      // bubble + one avatar per assistant segment, with markdown over full text.
      const last = state[state.length - 1];
      if (last && last.role === 'assistant') {
        const next = state.slice();
        next[next.length - 1] = {
          ...last,
          text: action.replace ? action.text : last.text + action.text,
          streaming: !action.done,
        };
        return next;
      }
      return [
        ...state,
        {
          id: action.messageId,
          role: 'assistant',
          text: action.text,
          streaming: !action.done,
          createdAt: Date.now(),
        },
      ];
    }
    case 'subagent-start': {
      const last = state[state.length - 1];
      const entry = {
        childSessionId: action.childSessionId,
        agent: action.agent,
        title: action.title,
        taskPrompt: action.prompt,
        outputText: '',
        status: 'streaming' as const,
      };
      if (last && last.role === 'assistant') {
        const next = state.slice();
        next[next.length - 1] = {
          ...last,
          streaming: true,
          subagents: { ...(last.subagents ?? {}), [action.childSessionId]: entry },
        };
        return next;
      }
      return [
        ...state,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          text: '',
          streaming: true,
          createdAt: Date.now(),
          subagents: { [action.childSessionId]: entry },
        },
      ];
    }
    case 'subagent-text': {
      // Resolve the assistant message that OWNS this child by `childSessionId`
      // (the same way lead `text` keys by `messageId`), NOT merely the last
      // one — a later lead message can start a new bubble after
      // `subagent-start` attached the child, and its deltas must still land on
      // the owning turn.
      let idx = state.findIndex(
        (m) => m.role === 'assistant' && !!m.subagents?.[action.childSessionId],
      );
      if (idx === -1) {
        // Defensive fallback for a stray `text` event ahead of
        // `subagent-start` (shouldn't happen per the express-code protocol, but
        // never drop content silently): synthesize onto the current last
        // assistant bubble.
        const lastIdx = state.length - 1;
        if (lastIdx < 0 || state[lastIdx].role !== 'assistant') return state;
        idx = lastIdx;
      }
      const target = state[idx];
      const existing = target.subagents?.[action.childSessionId] ?? {
        childSessionId: action.childSessionId,
        agent: null,
        title: null,
        taskPrompt: null,
        outputText: '',
        status: 'streaming' as const,
      };
      const next = state.slice();
      next[idx] = {
        ...target,
        subagents: {
          ...(target.subagents ?? {}),
          [action.childSessionId]: {
            ...existing,
            outputText: action.replace ? action.text : existing.outputText + action.text,
          },
        },
      };
      return next;
    }
    case 'subagent-end': {
      // Resolve by `childSessionId`, same as `subagent-text` — the owning turn
      // may no longer be the last assistant message.
      const idx = state.findIndex(
        (m) => m.role === 'assistant' && !!m.subagents?.[action.childSessionId],
      );
      if (idx === -1) return state;
      const target = state[idx];
      const next = state.slice();
      next[idx] = {
        ...target,
        subagents: {
          ...target.subagents,
          [action.childSessionId]: {
            ...target.subagents![action.childSessionId],
            status: 'done',
          },
        },
      };
      return next;
    }
    case 'stream-end': {
      const last = state[state.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        const next = state.slice();
        next[next.length - 1] = { ...last, streaming: false };
        return next;
      }
      return state;
    }
    case 'merge-history': {
      // Preserve EVERY message this reducer still considers `streaming`,
      // wherever it sits in the array — not just a contiguous trailing run.
      // A `getHistory` reconcile is a point-in-time server snapshot that can
      // lag the live stream, so overwriting an in-flight bubble (or anything
      // appended alongside/after it, e.g. a trailing tool row) would
      // flash/clobber it. Once it settles (a later `stream-end`/
      // `text{done:true}`), the NEXT reconcile naturally subsumes it into
      // the fresh history instead (no permanent duplicate).
      const streamingIndex = state.findIndex((m) => m.streaming);
      if (streamingIndex === -1) {
        // Nothing in flight locally — the incoming snapshot is authoritative,
        // same full-replace behavior as before this fix.
        return action.messages;
      }
      // The reconcile debounce isn't reset when a turn completes or a new
      // one starts, so by the time it fires, local state may already
      // include settled messages the fetched `history` predates — e.g. a
      // follow-up user message sent inside the debounce window, before the
      // NEXT turn started streaming. Trust `history` for everything it
      // covers, but fall back to the local copy for anything between where
      // `history` ends and where the still-streaming turn begins, so a stale
      // snapshot can never silently drop it. Always CAP at `streamingIndex`
      // even when `history` is long enough to reach or pass it — if the
      // server has already persisted a (possibly partial) copy of the
      // in-flight turn, appending the local streaming tail on top of the
      // untrimmed `history` would render that turn twice: once frozen from
      // `history`, once still typing from local state.
      const settledPrefix =
        action.messages.length >= streamingIndex
          ? action.messages.slice(0, streamingIndex)
          : [...action.messages, ...state.slice(action.messages.length, streamingIndex)];
      return [...settledPrefix, ...state.slice(streamingIndex)];
    }
    default:
      return state;
  }
}

/** Convert backend `HistoryMessage[]` into reducer-ready `ChatMessage[]`,
 *  substituting the clean display text for the first user message (the seed)
 *  so the hidden guardrail sent to the agent is never shown in chat. Shared by
 *  every history-load path (initial cache-hit load, veda reconnect reload,
 *  express tail-fallback load, express history-refresh/reconcile merge) so
 *  they all produce identical shapes. */
function toChatMessages(history: HistoryMessage[], displayOverride: string | null): ChatMessage[] {
  return history.map((m, i) => ({
    id: `h-${i}`,
    role: m.role,
    text: i === 0 && m.role === 'user' && displayOverride ? displayOverride : m.text,
    subagents: m.subagents,
  }));
}

interface UseTriageChatArgs {
  baseUrl: string;
  token: string;
  context: TriageContext;
  backend: TriageBackend;
  /** False for a click-opened session (any source other than the deep link):
   *  the seed effect below stays inert and `send` attaches the hidden
   *  guardrail to the user's own first message instead. Defaults to `true` so
   *  every existing call site (the deep-link route, and every test that
   *  doesn't pass it) keeps today's auto-seed-on-mount behavior unchanged. */
  autoSeed?: boolean;
}

export function useTriageChat({
  baseUrl,
  token,
  context,
  backend,
  autoSeed = true,
}: UseTriageChatArgs) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  // express-code surfaces errors via `useExpressChatSSE`'s `onError` callback
  // (there is no returned `error` field, unlike `useVedaChatSocket`), so we
  // need local state to hold whatever it last reported.
  const [expressError, setExpressError] = useState<string | null>(null);
  const [messages, dispatch] = useReducer(reducer, []);
  const seededRef = useRef(false);
  const didCreateRef = useRef(false);
  // When we reuse a session that already has history, skip the seeded prompt.
  const skipSeedRef = useRef(false);
  // The visible seed text (buildTriagePrompt output). Stored so that when
  // history is reloaded (cache hit or reconnect), we show the clean display
  // text instead of the full sent text which includes the hidden guardrail.
  const seedDisplayRef = useRef<string | null>(null);
  // Gate the seed until session setup (create OR reuse+history-load, OR for
  // express, reuse+reconnect-probe) resolves, so a fast transport connect
  // can't fire the seed before we know it's a reused session (which would
  // duplicate the prompt / fork context).
  const [setupResolved, setSetupResolved] = useState(false);
  // True only while an express-code mount-time `/tail` resume probe (+ its
  // fallback `getHistory` load, if the probe found nothing in flight) is
  // outstanding. Always false for the veda-ai backend — its own
  // reconnect-and-reload flow (`onReconnect` below) is unchanged and doesn't
  // need a distinct "probing" state, since the socket already reports
  // `connected`.
  const [reconnecting, setReconnecting] = useState(false);
  // Set during the mount effect when a cached express session needs its
  // mount-time reconnect probe; consumed by the effect below once `sessionId`
  // (an async session id, same as veda) actually becomes available.
  const needsAttachTailRef = useRef(false);
  const reconnectAttemptedRef = useRef(false);

  // Reuse ONE session per resource (persisted in sessionStorage). Any remount —
  // React StrictMode, parent re-render, modal reopen, or a flag-readiness flip —
  // then lands on the SAME session and LOADS its history, so follow-ups keep
  // context and the conversation stays continuous (this is the fix for "2nd
  // question starts a fresh chat"). Keyed by resource so different resources get
  // their own conversation.
  // Include runId so notifications for the same resource but different runs each
  // get their own session — without this, run 2222 would find run 1111's cached
  // session, hit skipSeedRef, and open with no context.
  // Prefixed with `backend.storageKeyPrefix` (R1) so a veda-ai session id
  // cached under one prefix is never handed to the OTHER backend's history
  // endpoint (which would 404, or worse, resolve to an unrelated session).
  const runSuffix = context.runId ? `.${context.runId}` : '';
  const storageKey = `${backend.storageKeyPrefix}.${context.resourceType}.${context.resourceId}${runSuffix}`;
  // Separate key for the seed display text so history reloads show the clean
  // visible prompt (not the full sent text which includes the hidden guardrail).
  const displayKey = `${storageKey}.display`;

  useEffect(() => {
    if (didCreateRef.current) return;
    didCreateRef.current = true;

    let cachedId: string | null = null;
    try {
      cachedId = window.sessionStorage.getItem(storageKey);
    } catch {
      cachedId = null;
    }
    let cachedDisplay: string | null = null;
    try {
      cachedDisplay = window.sessionStorage.getItem(displayKey);
    } catch {
      cachedDisplay = null;
    }

    if (cachedId) {
      seedDisplayRef.current = cachedDisplay;
      setSessionId(cachedId);
      skipSeedRef.current = true;

      if (backend.kind === 'express') {
        // Defer to the effect below: it waits for `sessionId` to actually
        // reach the express transport hook (an async id, same timing concern
        // as veda) before calling `attachTail()`, then falls back to
        // `getHistory` if nothing was in flight.
        needsAttachTailRef.current = true;
        setReconnecting(true);
        return;
      }

      backend
        .getHistory(cachedId)
        .then((history) => {
          if (history.length > 0) {
            dispatch({ type: 'init', messages: toChatMessages(history, cachedDisplay) });
          } else {
            // Session exists but no messages have persisted yet (write lag,
            // expired session, or first open after a crash). Re-enable the
            // seed so the agent always gets context on an empty chat.
            skipSeedRef.current = false;
          }
          setSetupResolved(true);
        })
        .catch(() => {
          // On API error fall back to re-seeding — better than a blank chat.
          skipSeedRef.current = false;
          setSetupResolved(true);
        });
      return;
    }

    backend
      .createSession(context)
      .then(({ sessionId: id }) => {
        setSessionId(id);
        try {
          window.sessionStorage.setItem(storageKey, id);
        } catch {
          /* storage unavailable — still works for this mount */
        }
        // Only the deep-link auto-seed flow ever sends the seed's hidden
        // guardrail text as history[0] — a click-opened (autoSeed: false)
        // session's real first turn is whatever the user types, sent via
        // `send()` below. Populating seedDisplayRef unconditionally here would
        // make every later history load (reconcile, /tail fallback, reconnect,
        // reopen-from-cache) substitute the deep-link seed text over the
        // user's own words, fabricating a message they never sent.
        if (autoSeed) {
          const seed = buildTriageSeed(context);
          seedDisplayRef.current = seed.display;
          try {
            window.sessionStorage.setItem(displayKey, seed.display);
          } catch {
            /* storage unavailable — still works for this mount */
          }
        }
        setSetupResolved(true);
      })
      .catch((e: unknown) => {
        setSetupError(e instanceof Error ? e.message : String(e));
        setSetupResolved(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend, storageKey, displayKey, autoSeed]);

  // Coalesce rapid text deltas into one dispatch per animation frame so the
  // streaming bubble doesn't re-render on every websocket chunk. We accumulate
  // the buffered delta text in a ref and flush it on requestAnimationFrame; a
  // `done` delta forces a synchronous flush so the final chunk is never
  // dropped. jsdom provides requestAnimationFrame, but we fall back to a
  // synchronous flush if it's missing.
  const deltaBufRef = useRef('');
  const deltaMessageIdRef = useRef<string | null>(null);
  const rafRef = useRef<number | null>(null);

  const flushDeltas = useCallback((done: boolean) => {
    if (rafRef.current != null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = null;
    const text = deltaBufRef.current;
    const messageId = deltaMessageIdRef.current;
    if (text === '' && !done) return;
    deltaBufRef.current = '';
    dispatch({ type: 'text', messageId: messageId ?? `a-${Date.now()}`, text, done });
  }, []);

  const onVedaEvent = useCallback(
    (e: ServerEvent) => {
      if (e.kind === 'text') {
        deltaBufRef.current += e.text;
        deltaMessageIdRef.current = e.messageId;
        if (e.done) {
          // Final chunk: flush immediately so the completed bubble is committed
          // (and `streaming` clears) without waiting on a frame.
          flushDeltas(true);
        } else if (typeof requestAnimationFrame === 'function') {
          if (rafRef.current == null) {
            rafRef.current = requestAnimationFrame(() => {
              rafRef.current = null;
              flushDeltas(false);
            });
          }
        } else {
          flushDeltas(false);
        }
      } else if (e.kind === 'tool') {
        // A tool message breaks the assistant delta run; flush any buffered
        // text first so it lands in its own bubble before the tool row.
        flushDeltas(false);
        dispatch({ type: 'tool', label: e.label });
      }
    },
    [flushDeltas],
  );

  // When a dropped connection is re-established, veda-ai rebuilds the agent's
  // context from persisted history. Reload that history so the transcript shown
  // matches what the agent now remembers (it self-heals a mid-conversation
  // drop). Only replace messages when the server actually returns history, so a
  // not-yet-persisted tail can't blank out the current UI.
  const onReconnect = useCallback(() => {
    if (!sessionId) return;
    const seedDisplay = seedDisplayRef.current;
    backend
      .getHistory(sessionId)
      .then((history) => {
        if (history.length > 0) {
          dispatch({ type: 'init', messages: toChatMessages(history, seedDisplay) });
        }
      })
      .catch(() => {
        /* keep current UI on failure */
      });
  }, [backend, sessionId]);

  // Debounced `getHistory` reconcile — triggered either by an in-turn
  // `history-refresh` signal or by a settled post-`/tail` resume (R5). Merges
  // via the reducer's `merge-history` case, which never overwrites a message
  // still marked `streaming`.
  const historyReconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleHistoryReconcile = useCallback(() => {
    const sid = sessionId;
    if (!sid) return;
    if (historyReconcileTimerRef.current) clearTimeout(historyReconcileTimerRef.current);
    historyReconcileTimerRef.current = setTimeout(() => {
      historyReconcileTimerRef.current = null;
      backend
        .getHistory(sid)
        .then((history) => {
          dispatch({
            type: 'merge-history',
            messages: toChatMessages(history, seedDisplayRef.current),
          });
        })
        .catch(() => {
          /* keep current UI on failure */
        });
    }, HISTORY_RECONCILE_DEBOUNCE_MS);
  }, [backend, sessionId]);

  useEffect(() => {
    return () => {
      if (historyReconcileTimerRef.current) clearTimeout(historyReconcileTimerRef.current);
    };
  }, []);

  const onExpressEvent = useCallback(
    (e: ExpressChatEvent) => {
      switch (e.kind) {
        case 'text':
          if (e.childSessionId) {
            dispatch({
              type: 'subagent-text',
              childSessionId: e.childSessionId,
              text: e.text,
              replace: e.replace,
            });
          } else {
            // Prefer OpenCode's real message id so distinct assistant messages
            // in one turn render as separate bubbles; fall back to a synthetic
            // id only if the wire event lacked one (keeps the single-bubble
            // behavior rather than dropping text).
            if (!deltaMessageIdRef.current) {
              deltaMessageIdRef.current = `a-${Date.now()}`;
            }
            dispatch({
              type: 'text',
              messageId: e.messageId ?? deltaMessageIdRef.current,
              text: e.text,
              done: false,
              replace: e.replace,
              partId: e.partId,
            });
          }
          break;
        case 'subagent-start':
          dispatch({
            type: 'subagent-start',
            childSessionId: e.childSessionId,
            agent: e.agent,
            title: e.title,
            prompt: e.prompt,
          });
          break;
        case 'subagent-end':
          dispatch({ type: 'subagent-end', childSessionId: e.childSessionId });
          break;
        case 'done':
          dispatch({ type: 'stream-end' });
          break;
        case 'history-refresh':
          scheduleHistoryReconcile();
          break;
        case 'error':
          // Surfaced via `onError` (setExpressError) — nothing extra here.
          break;
        case 'ready':
        case 'ignore':
        default:
          break;
      }
    },
    [scheduleHistoryReconcile],
  );

  // Both transport hooks are called on EVERY render — React's rules of hooks
  // forbid calling one or the other conditionally. Only the hook matching
  // `backend.kind` is fed a live `sessionId`; the other gets `null`, which
  // both hooks treat as fully inert (see useVedaChatSocket.ts's `connect()`
  // early-return and useExpressChatSSE.ts's lack of any sessionId-driven
  // connect effect), so it never opens a socket / fires a request. Only the
  // active branch's imperative methods (`sendUserMessage`/`stopGeneration` vs
  // `sendUserMessage`/`attachTail`/`abort`) are ever called below.
  const vedaSessionId = backend.kind === 'veda' ? sessionId : null;
  const expressSessionId = backend.kind === 'express' ? sessionId : null;

  const socket = useVedaChatSocket({
    baseUrl,
    sessionId: vedaSessionId,
    token,
    onEvent: onVedaEvent,
    onReconnect,
  });

  const expressHook = useExpressChatSSE({
    baseUrl,
    token,
    sessionId: expressSessionId,
    onEvent: onExpressEvent,
    onError: setExpressError,
  });

  // Mount-time reconnect probe for a REUSED express-code session (R5): once
  // `sessionId` actually reaches the express hook, ask `/tail` whether a turn
  // was still in flight before falling back to a normal history load.
  useEffect(() => {
    if (backend.kind !== 'express') return;
    if (!needsAttachTailRef.current) return;
    if (!sessionId) return;
    if (reconnectAttemptedRef.current) return;
    reconnectAttemptedRef.current = true;

    // Load settled history FIRST so the prior transcript renders immediately,
    // THEN attach `/tail` to resume any still-in-flight turn on top — the same
    // order express-code's own chat pane uses (loadHistory → attachTail).
    // Probing `/tail` first (the old order) left a mid-turn reopen blank until
    // the live stream settled, because the modal unmounts on close and reopens
    // from empty local state.
    (async () => {
      let historyLoaded = 0;
      try {
        const history = await backend.getHistory(sessionId);
        historyLoaded = history.length;
        if (history.length > 0) {
          dispatch({ type: 'init', messages: toChatMessages(history, seedDisplayRef.current) });
        }
      } catch {
        /* history failed — /tail may still resume a live turn; keep going */
      }
      let hadLiveEvents = false;
      try {
        ({ hadLiveEvents } = await expressHook.attachTail());
      } catch {
        /* tail failed — fall through to the seed decision below */
      }
      // A resumed turn renders live via onExpressEvent; the reconnectNonce
      // effect below then debounces a `getHistory` reconcile once it settles
      // (opencode's event bus has no replay). Only when there was NO prior
      // history AND nothing in flight do we re-enable the seed, so the agent
      // still gets context on a genuinely empty session.
      if (historyLoaded === 0 && !hadLiveEvents) {
        skipSeedRef.current = false;
      }
    })().finally(() => {
      setReconnecting(false);
      setSetupResolved(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend, sessionId, expressHook.attachTail]);

  // A `/tail` resume that DID find a live turn has no replay for anything
  // that started+finished entirely during the disconnect gap — once the
  // resumed stream settles (`reconnectNonce` bumps AND `streaming` drops back
  // to false, which the hook updates together in the same state transition),
  // debounce one `getHistory` reconcile to catch up on whatever was missed.
  const lastHandledNonceRef = useRef(0);
  useEffect(() => {
    if (backend.kind !== 'express') return;
    if (expressHook.streaming) return;
    if (expressHook.reconnectNonce === lastHandledNonceRef.current) return;
    lastHandledNonceRef.current = expressHook.reconnectNonce;
    scheduleHistoryReconcile();
  }, [backend, expressHook.reconnectNonce, expressHook.streaming, scheduleHistoryReconcile]);

  // `displayText` lets the seed send a fuller prompt to the agent (incl. the
  // hidden guardrail) while showing only the human-facing request in the chat.
  // Normal sends omit it, so what's sent == what's shown.
  const send = useCallback(
    (text: string, displayText?: string) => {
      if (!sessionId) return;
      // "First turn" = the seed hasn't fired yet on THIS mount. Computed
      // before we flip `seededRef.current` below so the seed call itself is
      // correctly `true` and every later call — including a reused-session
      // mount that skips seeding entirely — is `false` (the session's mode
      // was already locked on an earlier mount/turn).
      const isFirstTurn = !seededRef.current;
      seededRef.current = true;
      // When autoSeed is off (a click-opened session, not a deep link), the
      // agent never gets the deep-link-style seed — so the FIRST user-driven
      // turn silently carries the same hidden guardrail the seed would have
      // sent, appended to whatever the user actually asked. A caller that
      // already built its own sent/display pair (the seed effect below, or
      // the "Check current status" button) passes `displayText` explicitly
      // and is left untouched — never double-attach.
      const toSend =
        isFirstTurn && displayText === undefined && !autoSeed
          ? `${text} ${buildTriageGuardrail(context)}`
          : text;
      // Record what the user actually saw for this first click-opened turn —
      // `text`/`displayText`, never `toSend` — so a later history reload
      // (toChatMessages, below) substitutes this instead of falling through to
      // the raw server-stored text. That raw text carries the hidden guardrail
      // for all three click-opened first turns: appended above for a typed
      // question/starter chip, or already baked into `text` for "Check current
      // status"'s seed.sent. Without this, reopening the session renders the
      // guardrail text in the user's own bubble. Mirrors the autoSeed branch's
      // own seedDisplayRef/displayKey write in the mount effect above.
      if (isFirstTurn && !autoSeed) {
        const shown = displayText ?? text;
        seedDisplayRef.current = shown;
        try {
          window.sessionStorage.setItem(displayKey, shown);
        } catch {
          /* storage unavailable — still works for this mount */
        }
      }
      if (backend.kind === 'express') {
        // Fresh id per turn so two different assistant bubbles never share a
        // React key even if a previous turn's stream never reached `done`.
        deltaMessageIdRef.current = null;
        // Clear a stale error from an earlier turn — same as
        // useVedaChatSocket.sendUserMessage resetting `error: null` on send —
        // so a banner from turn 1 doesn't linger after turn 2 succeeds.
        setExpressError(null);
        expressHook.sendUserMessage(toSend, isFirstTurn);
        dispatch({ type: 'user', text: displayText ?? text });
      } else if (socket.sendUserMessage(toSend)) {
        dispatch({ type: 'user', text: displayText ?? text });
      }
    },
    [backend.kind, sessionId, expressHook, socket, autoSeed, context, displayKey],
  );

  // Fire the seeded triage prompt once, after setup resolves and the
  // transport is ready — UNLESS we reused a session that already has history
  // (then the existing conversation is shown and we must not re-seed).
  useEffect(() => {
    if (seededRef.current || !setupResolved || !sessionId) return;
    if (backend.kind === 'express') {
      // Wait for the mount-time reconnect probe (if any) to resolve first —
      // seeding on top of a still-in-flight resumed turn would fork context.
      if (reconnecting) return;
    } else if (!socket.connected) {
      return;
    }
    if (skipSeedRef.current) {
      seededRef.current = true;
      return;
    }
    // Click-opened session (not a deep link): leave `seededRef` false and
    // wait for the user to pick a suggested action or type their own
    // question — `send` attaches the hidden guardrail to that first turn.
    if (!autoSeed) return;
    // Send the full seed (request + hidden guardrail) to the agent, but only
    // show the request in the chat.
    const seed = buildTriageSeed(context);
    send(seed.sent, seed.display);
  }, [
    setupResolved,
    sessionId,
    backend.kind,
    reconnecting,
    socket.connected,
    context,
    send,
    autoSeed,
  ]);

  const stop = useCallback(() => {
    if (backend.kind === 'express') {
      // express-code's real stop mechanism is the SSE hook's own `abort()`
      // (local abort + best-effort server-side `/abort` call) — NOT
      // `backend.abortSession()`, which callers should still be free to use
      // elsewhere, but isn't the live-turn cancellation path this button
      // drives.
      expressHook.abort();
    } else {
      // veda-ai's real stop mechanism is the live socket's `stopGeneration()`
      // — `vedaBackend.abortSession()` is a documented no-op (it has no
      // access to the open WebSocket), so it must never be called here.
      socket.stopGeneration();
    }
  }, [backend.kind, expressHook, socket]);

  const streaming = backend.kind === 'express' ? expressHook.streaming : socket.streaming;
  const waitingForReady = backend.kind === 'express' ? expressHook.waitingForReady : false;
  // veda-ai: `connected` keeps its original meaning (WebSocket open).
  // express-code has no persistent connection, so `connected` is derived as
  // "a session exists AND idle and not blocked on anything" — exposed
  // alongside the raw `streaming`/`waitingForReady`/`reconnecting` pieces (not
  // collapsed away) so a consumer that needs to distinguish "sandbox booting"
  // from "resuming" from "a turn is streaming" still can. The `!!sessionId`
  // check matters for a BRAND NEW session: streaming/waitingForReady/
  // reconnecting are all false during the createSession round trip too (only
  // the cached-session reconnect probe sets `reconnecting`), so without it
  // `connected` would be true before there's a session to send to — the
  // empty-state buttons would look ready and silently no-op on the first
  // click (`send` bails out on `!sessionId`).
  const connected =
    backend.kind === 'express'
      ? !!sessionId && !streaming && !waitingForReady && !reconnecting
      : socket.connected;
  const error = setupError ?? (backend.kind === 'express' ? expressError : socket.error);

  return {
    messages,
    send,
    stop,
    connected,
    streaming,
    waitingForReady,
    reconnecting,
    error,
  };
}
